import { ICalendar, RecurringFrequency, WeekDay } from '../models/api/Calendar'
import servicesConfig, { CustomAxiosHeaders } from '../config/services'
import { requests } from '.'
import { AxiosResponse } from 'axios'

interface GetItemsByLocationId {
  locationId: string
}

interface RRule {
  freq: RecurringFrequency
  count: number
  interval?: number
  weekDays?: WeekDay[]
  monthDays?: number[]
}

interface ValidateCalendarSlugBody {
  groupId: string
  slug: string
  calendarId?: string
}

type GetCalendarEventsParams = {
  filter: {
    locationId: string
    userId?: string
    contactId?: string
    calendarId?: string
    groupId?: string
    startTime: string
    endTime: string
  },
  sort?: {
    field: 'dateAdded' | 'startTime'
    direction: 'asc' | 'desc'
  },
  includeAll?: boolean
}

type GetAppointmentsParams = {
  filter: {
    locationId: string
    userId?: string
    contactId?: string
    calendarId?: string
    groupId?: string
    appointmentStatus?: string
  },
  sort?: {
    field: 'dateAdded' | 'startTime'
    direction: 'asc' | 'desc'
  },
  startAfter?: string
  limit?: number
}

// Configure request setup
const serviceName = 'CalendarService'
const host = servicesConfig.Calendar
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Define service object with methods
const CalendarService = {
  getTeams: (params: GetItemsByLocationId) => 
    request().get('/teams', { params }).then(responseBody),
    
  getCalendars: (params: GetItemsByLocationId) => 
    request().get('/', { params }).then(responseBody),
    
  getIntegratedCalendars: (params: { locationId?: string; userId?: string; source: 'GOOGLE' | 'OUTLOOK' }) =>
    request().get('/external-calendars/get-calendars', { params }).then(responseBody),
    
  getCalendarEvents: (params: GetCalendarEventsParams) => 
    request().post('/events/get-calendar-events', params).then(responseBody),
    
  getAppointments: (params: GetAppointmentsParams) => 
    request().post('/events/get-appointments', params).then(responseBody),
    
  create: (body: ICalendar) => 
    request().post('/', body).then(responseBody),
    
  read: (id: string) => 
    request().get(`/${id}`).then(responseBody),
    
  update: (id: string, body: Partial<ICalendar>) => 
    request().put(`/${id}`, body).then(responseBody),
    
  isValidCalendarSlug: (body: ValidateCalendarSlugBody) => 
    request().post('/validate-calendar-slug', body).then(responseBody),
    
  generateRRuleText: (body: RRule) => 
    request().post('/generate-rrule-text', body).then(responseBody),
    
  fetchAppointment: (eventId: string) => 
    request().get(`/events/appointments/${eventId}`).then(responseBody),
    
  deleteAppointment: (eventId: string) => 
    request().delete(`/events/appointments/${eventId}`).then(responseBody),
    
  updateAppointment: (appointmentId: string, appointment: any) =>
    request().put(`/events/appointments/${appointmentId}`, appointment).then(responseBody),
    
  updateBlockSlot: (appointmentId: string, appointment: any) =>
    request().put(`/events/block-slots/${appointmentId}`, appointment).then(responseBody),
    
  getSubAccountConfig: (locationId: string) => 
    request().get(`/configuration/location/${locationId}`).then(responseBody),
    
  updateSubAccountConfig: (locationId: string, body: any) =>
    request().put(`/configuration/location/${locationId}`, body).then(responseBody),

  getAllServices:(locationId: string) => 
    request().get(`/services/all?locationId=${locationId}`).then(responseBody),

  getUserTimezonePreference: (locationId: string, userId: string) =>
    request().get(
      `/user-settings/${locationId}/${userId}/features`
    ).then(responseBody),
}

export default CalendarService
